// @ts-nocheck
import React from "react";
import { useState, useMemo } from "react";
import {
  Calendar,
  MoreHorizontal,
  ShoppingBag,
  AlertTriangle,
  CheckCircle2,
  Package2,
  Search,
  History,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Download,
  Plus,
  Printer,
  Split,
  Cpu,
  RotateCw,
  Building2,
  Boxes,
  Layers,
  Zap,
  Box,
  ArrowDownToLine,
  PackageCheck,
  Filter,
  FileText,
  Film,
  Droplet,
  Wrench,
  Package,
  ChevronsUpDown,
} from "lucide-react";
import { MaterialIdentityCell } from "./MaterialIdentityCell";
import { toast } from "sonner";
import { exportToExcel, formatDateForFile } from "@/lib/excel.utils";
import { executeBulkAction } from "@/utils/bulkActionUtils";
import { trpcClient } from "@/lib/trpc";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DateGroupedTable,
  DateGroupToggleButton,
  extractDateKey,
} from "@/components/shared/DateGroupedTable";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { cn, formatNumber } from "@/lib/utils";
import {
  BulkActionBar,
  type BulkAction,
} from "@/components/shared/BulkActionBar";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { PurchaseRequestForm } from "./PurchaseRequestForm";
import { SplitMaterialDialog } from "./SplitMaterialDialog";
import { BatchCreatePODialog } from "./BatchCreatePODialog";
import { MaterialPlanningGroupingSwitch, MaterialGroupMode } from "./components/MaterialPlanningGroupingSwitch";
import { MaterialDemandDailyGroupedView } from "./components/MaterialDemandDailyGroupedView";
import { ShopfloorMaterialStagingDialog } from "@/components/planning/ShopfloorMaterialStagingDialog";
import { PaperAllocationAndSurplusHubPage } from "@/pages/planning/production-orders/PaperAllocationAndSurplusHubPage";
import MaterialSupplyBoard from "@/pages/logistics/material-supply/MaterialSupplyBoard";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ListPageShell,
  useServerListFilter,
} from "@/components/shared/ListPageShell";
import {
  CompactKpiRibbon,
  type CompactKpiItem,
} from "@/components/shared/CompactKpiRibbon";
import { type StatusSidebarItem } from "@/components/shared/StatusSidebar";
import { MaterialPlanningStatus } from "@/config/status-registry";
import { useExcelExport } from "@/hooks/useExcelExport";
import type { ExcelColumnDef } from "@/lib/excel.utils";

const EXPORT_COLUMNS: ExcelColumnDef[] = [
  { key: "materialId", label: "Mã Vật Tư", width: 16 },
  { key: "materialName", label: "Tên Vật Tư", width: 26 },
  { key: "neededQty", label: "Nhu cầu", width: 14, format: "number" },
  { key: "stockQty", label: "Tồn kho", width: 14, format: "number" },
  { key: "shortageQty", label: "Thiếu hụt", width: 14, format: "number" },
  { key: "status", label: "Trạng thái", width: 16 },
];
import { PrintModal } from "@/components/planning/print/PrintModal";
import { MaterialPlanningPrintContent } from "@/components/planning/print/MaterialPlanningPrintContent";
import {
  InlineFilterRow,
  CHECKBOX_COL_NULL,
} from "@/components/shared/InlineFilterRow";
import type { FilterField } from "@/components/shared/FilterBar";
import { MaterialPlanRowDetailSheet } from "./MaterialPlanRowDetailSheet";

const FILTER_FIELDS: FilterField[] = [
  {
    key: "materialName",
    label: "Vật tư",
    type: "text",
    placeholder: "Tên vật tư...",
  },
  {
    key: "status",
    label: "Trạng thái",
    type: "combobox-multi",
    options: Object.entries(MaterialPlanningStatus).map(([k, v]) => ({
      value: k,
      label: v.label,
    })),
  },
];

import { useRowSelection } from "@/hooks/useRowSelection";
import { useRoleAccess } from "@/hooks/useRoleAccess";
import {
  DataTableRow,
  DataTableRowCheckbox,
  DataTableSelectAllCheckbox,
} from "@/components/shared/DataTableRow";
import { ResponsiveDataView } from "@/components/shared/ResponsiveDataView";
import { emsTabClass } from "@/components/ui/ems-tabs";
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

export default function MaterialPlanningListPage() {
  const { hasPermission } = useRoleAccess();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const searchParams = useSearch({ strict: false }) as { poId?: string; q?: string; tab?: string };
  const poSearch = searchParams?.poId || searchParams?.q || "";

  type MaterialHubTab = "demand" | "paper" | "supply";
  const initialHubTab: MaterialHubTab =
    searchParams?.tab === "paper"
      ? "paper"
      : searchParams?.tab === "supply"
        ? "supply"
        : "demand";

  const [hubTab, setHubTab] = useState<MaterialHubTab>(initialHubTab);

  React.useEffect(() => {
    if (searchParams?.tab === "paper" || searchParams?.tab === "supply" || searchParams?.tab === "demand") {
      setHubTab(searchParams.tab as MaterialHubTab);
    }
  }, [searchParams?.tab]);

  const handleHubTabChange = (t: MaterialHubTab) => {
    setHubTab(t);
    navigate({
      search: (prev: any) => ({ ...prev, tab: t }),
      replace: true,
    });
  };

  const [activeTab, setActiveTab] = useState<"PO_DEMAND" | "MRP_SUMMARY">(
    // Mở từ lệnh sản xuất (tab=po / po_demand hoặc có poId) → xem nhu cầu theo lệnh
    searchParams?.tab === "po_demand" || searchParams?.tab === "po" || Boolean(searchParams?.poId) ? "PO_DEMAND" : "MRP_SUMMARY"
  );
  const [selectedCategory, setSelectedCategory] = useState<string>("ALL");
  // Tổng hợp cần mua gom theo ngày lập kế hoạch vật tư, có tổng phụ trên dòng nhóm như các trang danh sách khác
  const [isDateGrouped, setIsDateGrouped] = useState(true);
  const [detailItem, setDetailItem] = useState<any | null>(null);
  const [poStatusFilter, setPoStatusFilter] = useState<string>("ALL");
  const [poPage, setPoPage] = useState(1);
  const [poGroupByMode, setPoGroupByMode] = useState<MaterialGroupMode>("PO");
  const [poSortBy, setPoSortBy] = useState<"orderId" | "deadline" | "createdAt">("orderId");
  const [poSortOrder, setPoSortOrder] = useState<"asc" | "desc">("desc");
  const [expandedPoIds, setExpandedPoIds] = useState<Set<string>>(
    poSearch ? new Set([poSearch]) : new Set()
  );

  const toggleAllPoExpand = () => {
    if (expandedPoIds.size > 0) {
      setExpandedPoIds(new Set());
    } else if (poDemandData?.items) {
      setExpandedPoIds(new Set(poDemandData.items.map((p: any) => p.id)));
    }
  };

  React.useEffect(() => {
    setPoPage(1);
    if (poSearch) {
      setExpandedPoIds(new Set([poSearch]));
    }
  }, [poSearch]);
  const [stagingModalData, setStagingModalData] = useState<{
    open: boolean;
    productionOrderId: string;
    poCode: string;
    productName: string;
    customerName?: string;
    materialId: string;
    materialCode: string;
    materialName: string;
    unit?: string;
    qtyRequired: number;
    materialPlanId?: string;
  } | null>(null);

  const [prItem, setPrItem] = useState<any>(null);

  const extraFilterParams = useMemo(
    () => ({
      category: selectedCategory === "ALL" ? undefined : selectedCategory,
    }),
    [selectedCategory],
  );

  const {
    items,
    isLoading,
    error,
    filterValues,
    showFilter,
    currentPage,
    pageSize,
    totalItems,
    statusFilter,
    onStatusChange,
    onFilterChange,
    onPageChange,
    onPageSizeChange,
    onToggleFilter,
    onClearAll,
    sortField,
    sortOrder,
    handleSort,
  } = useServerListFilter<any>({
    queryKey: "material-planning",
    extraParams: extraFilterParams,
    queryFn: (params: any) =>
      trpcClient.materialPlanning.list.query(params) as any,
  });

  const allItems = items; // for backwards compatibility with filterTags
  const paginatedItems = items;

  const rowIds = (paginatedItems || []).map((p: any) => p.id);
  const rowSelection = useRowSelection(rowIds);
  const { selectedIds, selectionMode, clearSelection, selectedCount } =
    rowSelection;

  const { handleExport, isExporting } = useExcelExport({
    filename: "ke_hoach_vat_tu",
    columns: EXPORT_COLUMNS,
    serverQuery: async (filters) => {
      const res = await trpcClient.materialPlanning.list.query({
        ...filters,
        ...extraFilterParams,
        limit: 5000,
        page: 1,
      });
      return res?.items || res || [];
    },
    filterValues,
    selectedIds: Array.from(selectedIds || []),
    localData: paginatedItems || [],
  });

  // Query dữ liệu Nhu cầu vật tư theo Lệnh Sản Xuất
  const {
    data: poDemandData,
    isLoading: isPoDemandLoading,
    refetch: refetchPoDemand,
  } = useQuery({
    queryKey: [
      "material-planning-po-demand",
      poPage,
      pageSize,
      poSearch,
      poStatusFilter,
      poSortBy,
      poSortOrder,
    ],
    queryFn: () =>
      trpcClient.materialPlanning.listByProductionOrders.query({
        page: poPage,
        // Cùng số dòng / trang người dùng chọn (trước cố định 20)
        limit: pageSize,
        search: poSearch || undefined,
        status: poStatusFilter === "ALL" ? undefined : poStatusFilter,
        poId: poSearch || undefined,
        sortBy: poSortBy,
        sortOrder: poSortOrder,
      }),
  });

  const togglePoExpand = (poIdOrOrderId: string) => {
    setExpandedPoIds((prev) => {
      const next = new Set(prev);
      if (next.has(poIdOrOrderId)) {
        next.delete(poIdOrOrderId);
      } else {
        next.add(poIdOrOrderId);
      }
      return next;
    });
  };

  const handleQuickAllocateOrIssue = async (poId: string, materialPlanIds?: string[]) => {
    try {
      const res = await trpcClient.materialPlanning.quickAllocateOrIssue.mutate({
        productionOrderId: poId,
        materialPlanIds,
        action: "ISSUE",
      });
      if (res.success) {
        if (res.needsPR) {
          toast.warning(res.message);
        } else {
          toast.success(res.message);
        }
      } else {
        toast.error(res.message);
      }
      await Promise.all([
        refetchPoDemand(),
        queryClient.invalidateQueries({ queryKey: ["material-planning"] }),
        queryClient.invalidateQueries({ queryKey: ["material-planning-stats"] }),
        queryClient.invalidateQueries({ queryKey: ["production-order"] }),
      ]);
    } catch (err: any) {
      toast.error(err?.message || "Lỗi khi cấp phát vật tư");
    }
  };

  const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);
  const [splitItem, setSplitItem] = useState<any>(null);
  const [isBatchPOOpen, setIsBatchPOOpen] = useState(false);
  const [itemsForPO, setItemsForPO] = useState<any[]>([]);
  const [isCalculatingMRP, setIsCalculatingMRP] = useState(false);

  // Thống kê 4 KPI Summary Cards
  const { data: statsData } = useQuery({
    queryKey: ["material-planning-stats"],
    queryFn: () => trpcClient.materialPlanning.stats.query(),
  });

  const handleRunMRP = async () => {
    setIsCalculatingMRP(true);
    try {
      const res = await trpcClient.materialPlanning.calculateAllMRP.mutate();
      toast.success(res.message || "Đã cân đối cung cầu MRP thành công!");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["material-planning"] }),
        queryClient.invalidateQueries({ queryKey: ["material-planning-stats"] }),
        refetchPoDemand(),
      ]);
    } catch (err: any) {
      toast.error(err?.message || "Lỗi khi tính toán cân đối MRP");
    } finally {
      setIsCalculatingMRP(false);
    }
  };

  const handleSplitMaterial = async (data: {
    id: string;
    qtyToSplit: number;
    splitReason?: string;
  }) => {
    try {
      await trpcClient.materialPlanning.splitMaterial.mutate(data);
      toast.success("Tách kế hoạch thành công");
      await queryClient.invalidateQueries({ queryKey: ["material-planning"] });
      await queryClient.invalidateQueries({ queryKey: ["material-planning-stats"] });
    } catch (err: any) {
      toast.error(err?.message || "Lỗi khi tách kế hoạch");
    }
  };


  const getBulkActions = (): BulkAction[] => {
    if (!selectedCount) return [];
    
    const actions: BulkAction[] = [
      {
        label: `Xác nhận Sẵn Sàng (${selectedCount})`,
        icon: <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />,
        onClick: async () => {
          const eligible = paginatedItems.filter((i: any) => selectedIds.has(i.id) && i.status !== "RECEIVED");
          if (eligible.length === 0) {
            toast.error("Không có dòng nào hợp lệ để Xác nhận sẵn sàng");
            return;
          }
          await executeBulkAction({
            selectedIds: eligible.map((i: any) => i.id),
            mutationFn: (id) => trpcClient.materialPlanning.update.mutate({ id: String(id), data: { status: "RECEIVED" } }),
            onSuccess: () => {
              clearSelection();
              queryClient.invalidateQueries({ queryKey: ["material-planning"] });
              queryClient.invalidateQueries({ queryKey: ["material-planning-stats"] });
            }
          });
        }
      },

      {
        label: `Tạo ĐMH (${selectedCount})`,
        icon: <ShoppingBag className="w-3.5 h-3.5" />,
        onClick: () => {
          const selectedData = paginatedItems.filter((i: any) =>
            selectedIds.has(i.id),
          );
          setItemsForPO(selectedData);
          setIsBatchPOOpen(true);
        },
      },
      {
        label: "Xuất Excel",
        icon: <Download className="w-3.5 h-3.5" />,
        onClick: () => {
          handleExport();
          clearSelection();
        },
      },
    ];
    return actions;
  };

  const handleCreatePR = async (formData: any) => {
    const qty = Number(formData.quantity);
    if (!qty || qty < 1) {
      toast.error("Số lượng phải lớn hơn 0");
      return;
    }
    const materialPlanId = formData.material_plan_id || prItem?.id || prItem?.materialPlanId || null;
    const materialId = formData.material_id || prItem?.plannedMaterialId || prItem?.materialId || null;
    if (!materialPlanId && !materialId) {
      toast.error("Vui lòng chọn vật tư hoặc kế hoạch vật tư");
      return;
    }
    try {
      const res = await trpcClient.materialPlanning.createPurchaseRequestFromPlan.mutate({
        materialPlanId,
        materialId,
        quantity: qty,
        requiredDate: formData.required_date || null,
        reason: formData.reason || null,
      });
      toast.success(res.message || "Tạo yêu cầu mua hàng thành công");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["material-planning"] }),
        queryClient.invalidateQueries({ queryKey: ["material-planning-stats"] }),
      ]);
      setPrItem(null);
    } catch (err: any) {
      toast.error(err?.message || "Không thể tạo yêu cầu mua hàng");
    }
  };

  const materialKpiItems = React.useMemo<CompactKpiItem[]>(() => [
    {
      key: "SUFFICIENT",
      label: "Đủ Tồn Kho",
      count: statsData?.sufficientCount ?? 0,
      icon: CheckCircle2,
      variant: "emerald",
      tooltip: "Vật tư sẵn sàng sản xuất",
    },
    {
      key: "SHORTAGE",
      label: "Thiếu Hụt Cần Mua",
      count: statsData?.shortageCount ?? 0,
      icon: AlertTriangle,
      variant: "rose",
      animate: (statsData?.shortageCount ?? 0) > 0,
      tooltip: "Cần lập PR / PO mua hàng gấp",
    },
    {
      key: "ORDERED",
      label: "Đang Đặt Hàng (PO)",
      count: statsData?.orderedCount ?? 0,
      icon: ShoppingBag,
      variant: "amber",
      tooltip: "Đang chờ nhà cung cấp giao",
    },
  ], [statsData]);

  const activeKpiKey = statusFilter
    ? Array.isArray(statusFilter)
      ? statusFilter[0]
      : statusFilter
    : null;

  const handleSelectKpi = (key: string | null) => {
    if (!key || key === "ALL") {
      onStatusChange("");
    } else {
      onStatusChange(statusFilter === key ? "" : key);
    }
  };

  const hubTabsControls = (
    <div role="tablist" aria-label="Nhóm kế hoạch vật tư" className="flex items-end gap-1 shrink-0 flex-nowrap">
      <button
        type="button"
        onClick={() => handleHubTabChange("demand")}
        role="tab"
        aria-selected={hubTab === "demand"}
        className={emsTabClass(hubTab === "demand", { size: "sm" })}
      >
        <Box className="w-3.5 h-3.5 text-indigo-600 shrink-0" />
        <span>1. Nhu Cầu Vật Tư</span>
      </button>
      <button
        type="button"
        onClick={() => handleHubTabChange("paper")}
        role="tab"
        aria-selected={hubTab === "paper"}
        className={emsTabClass(hubTab === "paper", { size: "sm" })}
      >
        <ArrowDownToLine className="w-3.5 h-3.5 text-blue-600 shrink-0" />
        <span>2. Cấp Phát Giấy</span>
      </button>
      <button
        type="button"
        onClick={() => handleHubTabChange("supply")}
        role="tab"
        aria-selected={hubTab === "supply"}
        className={emsTabClass(hubTab === "supply", { size: "sm" })}
      >
        <PackageCheck className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
        <span>3. Cấp NVL Sàn Máy</span>
      </button>
    </div>
  );

  if (hubTab === "paper") {
    return <PaperAllocationAndSurplusHubPage hubTabs={hubTabsControls} />;
  }

  if (hubTab === "supply") {
    return <MaterialSupplyBoard hubTabs={hubTabsControls} />;
  }

  return (
    <>
      <ListPageShell
        totalItems={activeTab === "PO_DEMAND" ? (poDemandData?.meta?.total ?? 0) : totalItems}
        currentPage={activeTab === "PO_DEMAND" ? poPage : currentPage}
        pageSize={pageSize}
        onPageChange={activeTab === "PO_DEMAND" ? setPoPage : onPageChange}
        onPageSizeChange={onPageSizeChange}
        filterFields={activeTab === "MRP_SUMMARY" ? FILTER_FIELDS : []}
        filterValues={filterValues}
        showFilter={showFilter}
        onToggleFilter={activeTab === "MRP_SUMMARY" ? onToggleFilter : undefined}
        onClearAll={onClearAll}
        tabs={hubTabsControls}
        headerFilters={
          <div className="flex items-center gap-1.5 flex-nowrap shrink-0">
            <div
              data-testid="mat-subview-toggle"
              role="tablist" className="inline-flex items-end gap-1 shrink-0 flex-nowrap"
            >
              <button
                type="button"
                onClick={() => setActiveTab("MRP_SUMMARY")}
                role="tab"
                aria-selected={activeTab === "MRP_SUMMARY"}
                className={emsTabClass(activeTab === "MRP_SUMMARY", { size: "sm" })}
              >
                <Layers className="w-3.5 h-3.5 text-indigo-600 shrink-0" />
                <span>Tổng Hợp Cần Mua</span>
              </button>
              <button
                type="button"
                onClick={() => setActiveTab("PO_DEMAND")}
                role="tab"
                aria-selected={activeTab === "PO_DEMAND"}
                className={emsTabClass(activeTab === "PO_DEMAND", { size: "sm" })}
              >
                <Boxes className="w-3.5 h-3.5 text-slate-600 shrink-0" />
                <span>Phân Bổ Lệnh SX</span>
              </button>
            </div>

            {activeTab === "MRP_SUMMARY" ? (
              <>
                <DateGroupToggleButton
                  isGrouped={isDateGrouped}
                  onToggle={() => setIsDateGrouped(!isDateGrouped)}
                />
                <Select
                  value={selectedCategory}
                  onValueChange={(val) => {
                    setSelectedCategory(val);
                    onPageChange(1);
                  }}
                >
                  <SelectTrigger
                    data-testid="mat-category-select"
                    aria-label="Loại vật tư"
                    className="data-[size=default]:h-7 min-w-0 px-2.5 gap-1 text-xs shrink-0 bg-white border-slate-200 w-[140px]"
                  >
                    <SelectValue placeholder="Loại vật tư" />
                  </SelectTrigger>
                  <SelectContent>
                    {[
                      { key: "ALL", label: "Tất Cả Loại VT" },
                      { key: "PAPER", label: "Giấy In" },
                      { key: "FILM", label: "Màng Cán" },
                      { key: "INK", label: "Mực In" },
                      { key: "GLUE", label: "Keo Dán" },
                      { key: "TOOLING", label: "Khuôn & Kẽm" },
                      { key: "OTHER", label: "Vật Tư Khác" },
                    ].map((cat) => (
                      <SelectItem key={cat.key} value={cat.key} className="text-xs">
                        {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <CompactKpiRibbon
                  items={materialKpiItems}
                  activeKey={activeKpiKey}
                  onSelect={handleSelectKpi}
                  isLoading={isLoading}
                  testIdPrefix="mat-kpi"
                  className="w-auto"
                />
              </>
            ) : (
              <>
                <MaterialPlanningGroupingSwitch
                  groupByMode={poGroupByMode}
                  onGroupByChange={(mode) => setPoGroupByMode(mode)}
                  sortBy={poSortBy}
                  sortOrder={poSortOrder}
                  onSortChange={(field, order) => {
                    setPoSortBy(field);
                    setPoSortOrder(order);
                    setPoPage(1);
                  }}
                />

                {poGroupByMode === "PO" && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={toggleAllPoExpand}
                    className="h-7 text-xs bg-white text-slate-700 hover:text-indigo-600 flex items-center gap-1 cursor-pointer shadow-2xs shrink-0"
                    title="Mở rộng hoặc thu gọn danh sách định mức của tất cả Lệnh sản xuất"
                  >
                    {expandedPoIds.size > 0 ? (
                      <>
                        <ChevronUp className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                        <span>Thu gọn</span>
                      </>
                    ) : (
                      <>
                        <ChevronDown className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                        <span>Mở rộng</span>
                      </>
                    )}
                  </Button>
                )}

                <Select
                  value={poStatusFilter}
                  onValueChange={(val) => {
                    setPoStatusFilter(val);
                    setPoPage(1);
                  }}
                >
                  <SelectTrigger className="!h-7 text-xs w-[140px] bg-white shadow-2xs shrink-0">
                    <SelectValue placeholder="Trạng thái" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">Tất cả trạng thái</SelectItem>
                    <SelectItem value="READY">✓ Sẵn sàng</SelectItem>
                    <SelectItem value="PARTIAL">⚠ Cấp một phần</SelectItem>
                    <SelectItem value="PENDING">⏳ Chờ cấp phát</SelectItem>
                  </SelectContent>
                </Select>
              </>
            )}
          </div>
        }
        onExport={() => {
          if (activeTab === "MRP_SUMMARY") {
            if (!isExporting) handleExport();
            return;
          }
          // Tab Phân bổ lệnh SX: xuất danh sách LSX đang hiển thị
          exportToExcel(
            poDemandData?.items || [],
            [
              { key: "orderId", label: "Lệnh SX", width: 18 },
              { key: "productName", label: "Sản phẩm", width: 36 },
              { key: "customerName", label: "Khách hàng", width: 24 },
              { key: "qtyPlan", label: "SL kế hoạch", width: 12, type: "number" },
              { key: "deadline", label: "Hạn", width: 14, format: (v: any) => (v ? new Date(v).toLocaleDateString("vi-VN") : "") },
              { key: "totalMaterials", label: "Tổng vật tư", width: 12, type: "number" },
              { key: "sufficientCount", label: "Vật tư đủ", width: 12, type: "number" },
              { key: "fulfillmentPercent", label: "Đáp ứng (%)", width: 12, type: "number" },
              { key: "materialStatus", label: "Trạng thái vật tư", width: 18 },
            ],
            `Phan-bo-vat-tu-LSX_${formatDateForFile()}`,
            { sheetName: "Phân bổ lệnh SX" }
          ).catch((e: any) => toast.error(e?.message || "Không xuất được Excel"));
        }}
        exportLoading={isExporting}
        secondaryActions={[
          {
            key: "print-material-plan",
            label: "In Kế Hoạch",
            icon: <Printer size={14} className="text-slate-600" />,
            onClick: () => setIsPrintModalOpen(true),
          },
        ]}
        actions={
          <div className="flex gap-1.5 items-center flex-nowrap shrink-0">
            {activeTab === "MRP_SUMMARY" && hasPermission("materialPlanning", "create") && (
              <Button
                size="sm"
                className="h-7 px-2.5 text-xs bg-indigo-600 hover:bg-indigo-700 text-white font-semibold shadow-xs shrink-0"
                onClick={handleRunMRP}
                disabled={isCalculatingMRP}
              >
                {isCalculatingMRP ? (
                  <RotateCw size={12} className="mr-1 animate-spin shrink-0" />
                ) : (
                  <Cpu size={12} className="mr-1 shrink-0" />
                )}
                <span>Cân Đối MRP</span>
              </Button>
            )}
            {hasPermission("materialPlanning", "create") && (
              <Button
                size="sm"
                variant="outline"
                className="h-7 px-2.5 text-xs shrink-0"
                onClick={() => setPrItem({})}
              >
                <Plus size={14} className="mr-1 shrink-0" />
                <span>Tạo PR</span>
              </Button>
            )}
          </div>
        }
      >
        {activeTab === "PO_DEMAND" ? (
          poGroupByMode === "DATE" ? (
            <MaterialDemandDailyGroupedView
              pos={poDemandData?.items || []}
              isLoading={isPoDemandLoading}
              onQuickAllocateOrIssue={handleQuickAllocateOrIssue}
              onOpenPRForm={(item) => {
                setPrItem({
                  id: item.materialPlanId || item.id,
                  materialName: item.materialName,
                  materialCode: item.materialCode,
                  unit: item.unit,
                  neededQty: item.qtyRequired,
                  stockQty: item.qtyAvailable,
                  shortageQty: item.shortageQty,
                });
              }}
              onNavigateToOrder={(poCode) =>
                navigate({
                  to: "/production-orders/$orderId",
                  params: { orderId: poCode },
                })
              }
            />
          ) : (
            <Card className="overflow-hidden border-slate-200 flex-1">
            <div className="overflow-x-auto">
              <Table className="min-w-[980px]">
                <TableHeader className="bg-slate-50 border-b border-slate-200">
                  <TableRow className="text-xs font-semibold text-slate-700 whitespace-nowrap">
                    <TableHead className="w-[44px] text-center whitespace-nowrap"></TableHead>
                    <TableHead className="w-[140px] whitespace-nowrap">Mã LSX</TableHead>
                    <TableHead className="min-w-[200px] whitespace-nowrap">Khách Hàng & Sản Phẩm</TableHead>
                    <TableHead className="w-[100px] text-right whitespace-nowrap">SL Kế Hoạch</TableHead>
                    <TableHead className="w-[100px] text-center whitespace-nowrap">Hạn Giao</TableHead>
                    <TableHead className="w-[180px] whitespace-nowrap">Tiến Độ Sẵn Sàng</TableHead>
                    <TableHead className="w-[130px] text-center whitespace-nowrap">Trạng Thái Vật Tư</TableHead>
                    <TableHead className="w-[180px] text-right whitespace-nowrap">Thao Tác</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isPoDemandLoading ? (
                    Array.from({ length: 5 }).map((_, i) => (
                      <TableRow key={i}>
                        {Array.from({ length: 8 }).map((_, j) => (
                          <TableCell key={j}>
                            <Skeleton className="h-4 w-20" />
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : poDemandData?.items?.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-12 text-sm text-slate-500">
                        Không tìm thấy lệnh sản xuất nào phù hợp
                      </TableCell>
                    </TableRow>
                  ) : (
                    poDemandData?.items?.map((po: any) => {
                      const isExpanded = expandedPoIds.has(po.id) || expandedPoIds.has(po.orderId);
                      const isFullyReady = po.materialStatus === "READY" || po.fulfillmentPercent === 100;
                      const hasShortage = po.shortageCount > 0;

                      return (
                        <React.Fragment key={po.id}>
                          <TableRow
                            className={cn(
                              "group transition-colors border-b border-slate-200 cursor-pointer",
                              isExpanded ? "bg-indigo-50/30" : "hover:bg-slate-50/70"
                            )}
                            onClick={() => {
                              if (window.getSelection()?.toString().trim()) return;
                              navigate({
                                to: "/production-orders/$orderId",
                                params: { orderId: po.orderId },
                                search: { tab: "materials" } as any,
                              });
                            }}
                          >
                            <TableCell className="text-center p-2" onClick={(e) => e.stopPropagation()}>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6 text-slate-500 group-hover:text-indigo-600"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  togglePoExpand(po.id);
                                }}
                              >
                                {isExpanded ? (
                                  <ChevronUp className="h-4 w-4" />
                                ) : (
                                  <ChevronDown className="h-4 w-4" />
                                )}
                              </Button>
                            </TableCell>

                            <TableCell>
                              <span className="font-mono text-sm font-bold text-indigo-700">
                                {po.orderId}
                              </span>
                            </TableCell>

                            <TableCell>
                              <div className="flex flex-col">
                                <span className="font-semibold text-xs text-slate-900 leading-tight">
                                  {po.productName}
                                </span>
                                <span className="text-[11px] text-slate-500 truncate max-w-[240px]">
                                  {po.customerName}
                                </span>

                                {po.shortageMaterials && po.shortageMaterials.length > 0 ? (
                                  <div className="flex flex-wrap gap-1 mt-1.5" onClick={(e) => e.stopPropagation()}>
                                    {po.shortageMaterials.slice(0, 3).map((sm: any) => (
                                      <Badge
                                        key={sm.id}
                                        variant="outline"
                                        className="text-[10px] px-1.5 py-0 font-mono border-rose-300 text-rose-700 bg-rose-50 flex items-center gap-1 shadow-2xs"
                                        title={`${sm.materialName} - Thiếu ${formatNumber(sm.shortageQty)} ${sm.unit}`}
                                      >
                                        <AlertTriangle className="w-2.5 h-2.5 text-rose-500 shrink-0" />
                                        <span className="font-bold">{sm.materialCode}</span>
                                        <span className="text-slate-500">: thiếu</span>
                                        <span className="font-bold text-rose-600">{formatNumber(sm.shortageQty)} {sm.unit}</span>
                                      </Badge>
                                    ))}
                                    {po.shortageMaterials.length > 3 && (
                                      <Badge
                                        variant="outline"
                                        className="text-[10px] px-1.5 py-0 text-slate-600 bg-slate-100 border-slate-300 cursor-pointer"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          togglePoExpand(po.id);
                                        }}
                                        title="Bấm để mở rộng xem toàn bộ vật tư thiếu"
                                      >
                                        +{po.shortageMaterials.length - 3} VT khác
                                      </Badge>
                                    )}
                                  </div>
                                ) : po.fulfillmentPercent === 100 ? (
                                  <div className="mt-1 flex items-center gap-1 text-[11px] text-emerald-600 font-medium">
                                    <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                                    <span>Đủ 100% vật tư</span>
                                  </div>
                                ) : null}
                              </div>
                            </TableCell>

                            <TableCell className="text-right font-mono font-medium text-xs text-slate-800">
                              {formatNumber(po.qtyPlan)} con
                            </TableCell>

                            <TableCell className="text-center text-xs text-slate-600 font-mono">
                              {po.deadline ? format(new Date(po.deadline), "dd/MM/yyyy") : "—"}
                            </TableCell>

                            <TableCell>
                              <div className="space-y-1 w-[160px]">
                                <div className="flex justify-between text-[10px] font-bold">
                                  <span className="text-slate-600">
                                    {po.sufficientCount}/{po.totalMaterials} vật tư
                                  </span>
                                  <span
                                    className={cn(
                                      po.fulfillmentPercent === 100
                                        ? "text-emerald-700 font-bold"
                                        : po.fulfillmentPercent > 0
                                        ? "text-amber-700 font-bold"
                                        : "text-slate-500"
                                    )}
                                  >
                                    {po.fulfillmentPercent}%
                                  </span>
                                </div>
                                <Progress
                                  value={po.fulfillmentPercent}
                                  className={cn(
                                    "h-2",
                                    po.fulfillmentPercent === 100
                                      ? "[&>div]:bg-emerald-600 bg-emerald-100"
                                      : po.fulfillmentPercent > 0
                                      ? "[&>div]:bg-amber-500 bg-amber-100"
                                      : "bg-slate-100"
                                  )}
                                />
                              </div>
                            </TableCell>

                            <TableCell className="text-center">
                              <Badge
                                className={cn(
                                  "border-none font-bold text-[10px] shadow-none",
                                  isFullyReady
                                    ? "bg-emerald-100 text-emerald-800"
                                    : po.materialStatus === "PARTIAL"
                                    ? "bg-amber-100 text-amber-800"
                                    : hasShortage
                                    ? "bg-rose-100 text-rose-800"
                                    : "bg-slate-100 text-slate-700"
                                )}
                              >
                                {isFullyReady
                                  ? "✓ SẴN SÀNG"
                                  : po.materialStatus === "PARTIAL"
                                  ? "⚠ CẤP MỘT PHẦN"
                                  : hasShortage
                                  ? "❌ THIẾU HỤT"
                                  : "⏳ CHỜ CẤP PHÁT"}
                              </Badge>
                            </TableCell>

                            <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                              <div className="flex items-center justify-end gap-1.5">
                                {!isFullyReady && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-7 text-xs bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100 font-semibold"
                                    onClick={() => handleQuickAllocateOrIssue(po.id)}
                                    title="Xác nhận cấp phát toàn bộ vật tư cho LSX này"
                                  >
                                    <Zap className="w-3 h-3 mr-1 text-emerald-600" />
                                    Cấp Đủ
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>

                          {/* Accordion Chi Tiết Nhu Cầu Vật Tư */}
                          {isExpanded && (
                            <TableRow className="bg-slate-50/70 border-b border-indigo-100">
                              <TableCell colSpan={8} className="p-3 pl-12 pr-4">
                                <div className="bg-white rounded-lg border border-slate-200 shadow-2xs overflow-hidden">
                                  <div className="p-2.5 bg-slate-100/70 border-b border-slate-200 flex items-center justify-between text-xs">
                                    <span className="font-semibold text-slate-800 flex items-center gap-1.5">
                                      <Package2 className="w-3.5 h-3.5 text-indigo-600" />
                                      Bảng Định Mức Vật Tư LSX {po.orderId} ({po.materials?.length || 0} vật tư)
                                    </span>
                                    <span className="text-[11px] text-slate-500">
                                      Bấm "Mua Hàng" nếu thiếu hụt hoặc "Cấp Phát" để xuất hàng sàn xưởng
                                    </span>
                                  </div>

                                  <Table className="text-xs">
                                    <TableHeader className="bg-slate-50">
                                      <TableRow className="text-slate-600 font-medium whitespace-nowrap">
                                        <TableHead colSpan={2} className="min-w-[220px]">Loại • Mã & Tên Vật Tư</TableHead>
                                        <TableHead className="w-[90px] text-right">Định Mức</TableHead>
                                        <TableHead className="w-[90px] text-right">Tồn Khả Dụng</TableHead>
                                        <TableHead className="w-[90px] text-right">Thiếu Hụt</TableHead>
                                        <TableHead className="w-[125px] text-center">Hạn Cần VT (Deadline)</TableHead>
                                        <TableHead className="w-[120px] text-center">Đơn Mua Hàng</TableHead>
                                        <TableHead className="w-[110px] text-center">Trạng Thái</TableHead>
                                        <TableHead className="w-[160px] text-right">Hành Động</TableHead>
                                      </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                      {po.materials?.length === 0 ? (
                                        <TableRow>
                                          <TableCell colSpan={9} className="text-center py-4 text-slate-500">
                                            Chưa có định mức vật tư cho lệnh này
                                          </TableCell>
                                        </TableRow>
                                      ) : (
                                        po.materials.map((mat: any) => {
                                          const isShortage = mat.shortageQty > 0;
                                          const isReceived = mat.status === "RECEIVED";

                                          return (
                                            <TableRow key={mat.id} className="hover:bg-slate-50">
                                              <TableCell colSpan={2}>
                                                <MaterialIdentityCell
                                                  materialCode={mat.materialCode}
                                                  materialName={mat.materialName}
                                                  category={mat.materialCategory}
                                                  typeCode={mat.typeCode}
                                                  typeLabel={mat.typeLabel}
                                                  suppliedSize={mat.suppliedSize}
                                                  unit={mat.unit}
                                                />
                                              </TableCell>

                                              <TableCell className="text-right font-mono font-medium">
                                                {formatNumber(mat.neededQty)} {mat.unit}
                                              </TableCell>

                                              <TableCell className="text-right font-mono text-slate-700">
                                                {formatNumber(mat.stockQty)} {mat.unit}
                                              </TableCell>

                                              <TableCell className="text-right font-mono font-bold">
                                                {isShortage ? (
                                                  <span className="text-rose-600 flex items-center justify-end gap-1">
                                                    <AlertTriangle className="w-3 h-3" />
                                                    {formatNumber(mat.shortageQty)}
                                                  </span>
                                                ) : (
                                                  <span className="text-emerald-600">0</span>
                                                )}
                                              </TableCell>

                                              <TableCell className="text-center font-mono text-xs whitespace-nowrap">
                                                {mat.expectedDate ? (
                                                  <div className="flex flex-col items-center">
                                                    <span className="font-semibold text-slate-700">
                                                      {format(new Date(mat.expectedDate), "dd/MM/yyyy")}
                                                    </span>
                                                    <span className="text-[10px] text-indigo-600 font-semibold bg-indigo-50 px-1.5 py-0.5 rounded">
                                                      {format(new Date(mat.expectedDate), "HH:mm")}
                                                    </span>
                                                  </div>
                                                ) : (
                                                  <span className="text-slate-400 text-[11px]">—</span>
                                                )}
                                              </TableCell>

                                              <TableCell className="text-center">
                                                {mat.purchaseOrders?.length > 0 ? (
                                                  <div className="flex flex-wrap gap-1 justify-center">
                                                    {mat.purchaseOrders.map((poNo: string, idx: number) => (
                                                      <Badge
                                                        key={idx}
                                                        variant="outline"
                                                        className="text-[9px] font-mono border-amber-300 text-amber-800 bg-amber-50"
                                                      >
                                                        {poNo}
                                                      </Badge>
                                                    ))}
                                                    {Number(mat.poIncomingQty) > 0 && (
                                                      <span className="w-full text-[10px] text-indigo-700 font-semibold">
                                                        Đang về {formatNumber(mat.poIncomingQty)} {mat.unit}
                                                      </span>
                                                    )}
                                                  </div>
                                                ) : (
                                                  <span className="text-slate-400 text-[10px]">—</span>
                                                )}
                                              </TableCell>

                                              <TableCell className="text-center">
                                                <Badge
                                                  className={cn(
                                                    "border-none text-[10px] font-bold shadow-none",
                                                    isReceived
                                                      ? "bg-emerald-100 text-emerald-800"
                                                      : mat.status === "ORDERED"
                                                      ? "bg-amber-100 text-amber-800"
                                                      : isShortage
                                                      ? "bg-rose-100 text-rose-800"
                                                      : "bg-slate-100 text-slate-700"
                                                  )}
                                                >
                                                  {isReceived
                                                    ? "ĐÃ CẤP"
                                                    : mat.status === "ORDERED"
                                                    ? "ĐÃ ĐẶT"
                                                    : isShortage
                                                    ? "THIẾU"
                                                    : "CHỜ CẤP"}
                                                </Badge>
                                              </TableCell>

                                              <TableCell className="text-right">
                                                <div className="flex items-center justify-end gap-1">
                                                  {isShortage && (
                                                    <Button
                                                      size="sm"
                                                      className="h-6 px-2 text-[11px] bg-amber-600 hover:bg-amber-700 text-white font-semibold shadow-2xs"
                                                      onClick={() =>
                                                        setPrItem({
                                                          id: mat.materialPlanId,
                                                          materialId: mat.plannedMaterialId || mat.originalMaterialId,
                                                          materialName: mat.materialName,
                                                          shortageQty: mat.shortageQty,
                                                          productionOrderId: po.id,
                                                        })
                                                      }
                                                      title="Mở form đặt mua vật tư thiếu hụt"
                                                    >
                                                      <ShoppingBag className="w-3 h-3 mr-1" />
                                                      Mua Hàng
                                                    </Button>
                                                  )}

                                                  {!isReceived && (
                                                    <Button
                                                      size="sm"
                                                      variant="outline"
                                                      className="h-6 px-2 text-[11px] border-indigo-200 text-indigo-700 hover:bg-indigo-50 font-semibold"
                                                      onClick={() =>
                                                        setStagingModalData({
                                                          open: true,
                                                          productionOrderId: po.id,
                                                          poCode: po.orderId,
                                                          productName: po.productName,
                                                          customerName: po.customerName,
                                                          materialId: mat.plannedMaterialId || mat.originalMaterialId,
                                                          materialCode: mat.materialCode,
                                                          materialName: mat.materialName,
                                                          unit: mat.unit,
                                                          qtyRequired: mat.neededQty,
                                                          materialPlanId: mat.materialPlanId,
                                                        })
                                                      }
                                                      title="Cấp phát vật tư này cho phân xưởng"
                                                    >
                                                      <Package2 className="w-3 h-3 mr-1" />
                                                      Cấp Phát
                                                    </Button>
                                                  )}
                                                </div>
                                              </TableCell>
                                            </TableRow>
                                          );
                                        })
                                      )}
                                    </TableBody>
                                  </Table>
                                </div>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Pagination PO Demand */}
            {poDemandData?.meta && (
              <div className="p-3 border-t border-slate-200 flex items-center justify-between text-xs text-slate-600 bg-white">
                <span>
                  Hiển thị trang {poDemandData.meta.page} / {poDemandData.meta.totalPages || 1} ({poDemandData.meta.total} Lệnh Sản Xuất)
                </span>
                <div className="flex items-center gap-1.5">
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs bg-white"
                    disabled={poDemandData.meta.page <= 1}
                    onClick={() => setPoPage((p) => Math.max(1, p - 1))}
                  >
                    <ChevronLeft className="w-3.5 h-3.5 mr-1" /> Trước
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs bg-white"
                    disabled={poDemandData.meta.page >= poDemandData.meta.totalPages}
                    onClick={() => setPoPage((p) => p + 1)}
                  >
                    Sau <ChevronRight className="w-3.5 h-3.5 ml-1" />
                  </Button>
                </div>
              </div>
            )}
          </Card>
          )
        ) : (
          /* Table MRP Summary */
          <Card className="overflow-hidden border-slate-200 flex-1">
            <ResponsiveDataView
              items={paginatedItems}
              emptyState="Không có dữ liệu kế hoạch vật tư"
              renderMobileCard={(item: any) => {
                const neededQty = item.neededQty || item.requiredQty || 0;
                const stockQty = item.stockQty || 0;
                const shortageQty = item.shortageQty || 0;
                const stockPct =
                  neededQty > 0 ? Math.min((stockQty / neededQty) * 100, 100) : 0;
                const statusCfg = MaterialPlanningStatus[item.status];
                const isUrgent =
                  item.status === "PENDING" || item.status === "CANCELLED";

                return (
                  <div
                    className={cn(
                      "flex flex-col bg-white border rounded-xl shadow-sm overflow-hidden active:scale-[0.99] transition-transform",
                      selectedIds.has(item.id)
                        ? "border-indigo-400 ring-1 ring-indigo-400"
                        : "border-slate-200",
                    )}
                    onClick={(e) => {
                      if (selectionMode) {
                        rowSelection.handleRowSelect(item.id, e);
                        return;
                      }
                      setDetailItem({ ...item, materialCode: item.materialCode || item.materialId, __statusLabel: statusCfg?.label || item.status });
                    }}
                  >
                    <div className="flex items-start justify-between p-3 border-b border-slate-100 bg-slate-50/50">
                      <div className="flex items-start gap-2 flex-1 min-w-0">
                        {selectionMode && (
                          <div onClick={(e) => e.stopPropagation()} className="pt-1">
                            <Checkbox
                              checked={selectedIds.has(item.id)}
                              onCheckedChange={() =>
                                rowSelection.handleRowSelect(item.id)
                              }
                            />
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <MaterialIdentityCell
                            materialCode={item.materialCode || item.materialId}
                            materialName={item.materialName}
                            category={item.materialCategory || item.category}
                            typeCode={item.typeCode}
                            typeLabel={item.typeLabel}
                            suppliedSize={item.suppliedSize}
                            unit={item.unit}
                          />
                        </div>
                      </div>
                      <Badge
                        className={cn(
                          "border-none font-bold text-[10px] shadow-none shrink-0",
                          statusCfg?.color || "bg-slate-100 text-slate-700",
                        )}
                      >
                        {statusCfg?.label || item.status}
                      </Badge>
                    </div>

                    <div className="p-3 flex flex-col gap-2">
                      <div className="flex justify-between items-center text-xs">
                        <span className="text-slate-500">Nhu cầu cần:</span>
                        <span className="font-bold text-slate-900 font-mono">
                          {formatNumber(neededQty)} {item.unit}
                        </span>
                      </div>

                    <div className="mt-1">
                      <div className="flex justify-between text-[10px] font-bold text-slate-500 mb-1">
                        <span>
                          Kho: {formatNumber(stockQty)} {item.unit}
                        </span>
                        <span>{Math.round(stockPct)}%</span>
                      </div>
                      <Progress
                        value={stockPct}
                        className={cn(
                          "h-1.5",
                          stockPct < 50 ? "bg-rose-100" : "bg-slate-100",
                        )}
                      />
                    </div>

                    <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-100">
                      <div className="flex flex-col">
                        <span className="text-[10px] text-slate-500">
                          Thiếu hụt
                        </span>
                        {shortageQty > 0 ? (
                          <span className="font-mono text-xs font-bold text-rose-600 flex items-center gap-1">
                            <AlertTriangle className="h-3 w-3" />{" "}
                            {formatNumber(shortageQty)}
                          </span>
                        ) : (
                          <span className="font-mono text-xs font-bold text-emerald-600 flex items-center gap-1">
                            <CheckCircle2 className="h-3 w-3" /> OK
                          </span>
                        )}
                      </div>
                      <div className="flex flex-col items-end">
                        <span className="text-[10px] text-slate-500">
                          Đơn hàng
                        </span>
                        <div className="flex gap-1 mt-0.5 flex-wrap">
                          {item.productionOrders
                            ?.slice(0, 2)
                            .map((po: any, idx: number) => {
                              const poId = typeof po === "object" ? po.orderId || po.id : po;
                              return (
                                <Badge
                                  key={idx}
                                  variant="outline"
                                  className="text-[10px] bg-blue-50 text-blue-700 border-blue-200 font-normal"
                                >
                                  {poId}
                                </Badge>
                              );
                            })}
                          {item.productionOrders?.length > 2 && (
                            <Badge
                              variant="outline"
                              className="text-[10px] bg-slate-50 font-normal"
                            >
                              +{item.productionOrders.length - 2}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            }}
          >
            <Table className={cn(selectionMode && "apv-select-mode")}>
              <TableHeader className="apv-table-header">
                <TableRow className="whitespace-nowrap">
                  <DataTableSelectAllCheckbox
                    rowSelection={rowSelection}
                    className={cn("apv-col-checkbox w-10 text-center sticky left-0 z-20 bg-white [isolation:isolate]")}
                  />
                  <TableHead className="min-w-[260px] text-slate-800 font-semibold">
                    Loại • Mã & Tên Vật Tư
                  </TableHead>
                  <TableHead className="w-[110px] text-right text-slate-800 font-semibold">
                    Tổng Nhu Cầu
                  </TableHead>
                  <TableHead className="w-[120px] text-right text-slate-800 font-semibold">
                    Tồn Khả Dụng
                  </TableHead>
                  <TableHead className="w-[110px] text-right text-slate-800 font-semibold">
                    Thiếu Hụt
                  </TableHead>
                  <TableHead className="w-[150px] text-slate-800 font-semibold">
                    Nhà Cung Cấp
                  </TableHead>
                  <TableHead className="w-[140px] text-slate-800 font-semibold">
                    Lệnh SX Cần Dùng
                  </TableHead>
                  <TableHead className="text-center w-[95px] text-slate-800 font-semibold">
                    Trạng Thái
                  </TableHead>
                  <TableHead className="w-[100px] text-right sticky right-0 z-20 bg-white [isolation:isolate] text-slate-800 font-semibold">
                    Thao Tác
                  </TableHead>
                </TableRow>
                {showFilter && (
                  <InlineFilterRow
                    columns={[
                      selectionMode ? CHECKBOX_COL_NULL : null,
                      { key: "materialName" },
                      null,
                      null,
                      null,
                      null,
                      null,
                      { key: "status" },
                      null,
                    ]}
                    filterFields={FILTER_FIELDS}
                    filterValues={filterValues}
                    onFilterChange={onFilterChange}
                  />
                )}
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  Array.from({ length: 6 }).map((_, i) => (
                    <TableRow key={i}>
                      {Array.from({ length: 9 }).map((_, j) => (
                        <TableCell key={j}>
                          <Skeleton className="h-3 w-14" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : paginatedItems.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={9}
                      className="text-center py-6 text-sm text-muted-foreground"
                    >
                      Không có dữ liệu kế hoạch vật tư
                    </TableCell>
                  </TableRow>
                ) : (
                  (() => {
                    const renderRow = (item: any, isChild: boolean = false) => {
                      const neededQty = item.neededQty || item.requiredQty || 0;
                      const stockQty = item.stockQty || 0;
                      const shortageQty = item.shortageQty || 0;
                      const stockPct =
                        neededQty > 0
                          ? Math.min((stockQty / neededQty) * 100, 100)
                          : 0;
                      const statusCfg =
                        MaterialPlanningStatus[
                          item.status as keyof typeof MaterialPlanningStatus
                        ];

                      const materialCode =
                        item.materialCode ||
                        item.originalMaterial?.code ||
                        item.plannedMaterial?.code ||
                        (item.materialId ? String(item.materialId).slice(0, 8) : undefined);
                      const materialName =
                        item.materialName ||
                        item.originalMaterial?.name ||
                        item.plannedMaterial?.name ||
                        "Vật tư";
                      const category =
                        item.materialCategory ||
                        item.typeCode ||
                        item.materialType?.code;
                      const typeLabel =
                        item.typeLabel ||
                        item.materialType?.label;
                      const suppliedSize =
                        item.suppliedSize ||
                        item.plannedMaterial?.specs ||
                        item.originalMaterial?.specs ||
                        item.specs;

                      return (
                        <DataTableRow
                          key={item.id}
                          id={item.id}
                          rowSelection={rowSelection}
                          className={cn(
                            "group hover:bg-blue-50/40 transition-colors",
                            isChild && "bg-slate-50/40",
                          )}
                          onClick={() => {
                            if (selectionMode) {
                              rowSelection.handleRowSelect(item.id);
                            } else {
                              // Xem chi tiết dòng nhu cầu: vật tư, cần / tồn / thiếu, NCC, các lệnh cần dùng
                              setDetailItem({ ...item, materialCode, materialName, __statusLabel: statusCfg?.label || item.status });
                            }
                          }}
                        >
                          <DataTableRowCheckbox
                            id={item.id}
                            rowSelection={rowSelection}
                            className={cn(
                              "apv-col-checkbox sticky left-0 z-10 bg-white [isolation:isolate]",
                            )}
                          />
                          <TableCell className="py-2.5">
                            <MaterialIdentityCell
                              materialCode={materialCode}
                              materialName={materialName}
                              category={category}
                              typeLabel={typeLabel}
                              suppliedSize={suppliedSize}
                              isSplitChild={isChild}
                              splitReason={item.splitReason}
                            />
                          </TableCell>
                          <TableCell className="text-right py-2.5">
                            <div className="font-semibold text-slate-800 text-xs font-mono">
                              {formatNumber(neededQty)}{" "}
                              <span className="text-[10px] font-normal text-slate-500">
                                {item.unit}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className="py-2.5">
                            <div className="space-y-1 w-[110px] ml-auto text-right">
                              <div className="flex justify-between text-[11px] font-medium text-slate-600">
                                <span>{Math.round(stockPct)}%</span>
                                <span className="font-bold text-slate-900 font-mono">
                                  {formatNumber(stockQty)} {item.unit}
                                </span>
                              </div>
                              <Progress
                                value={stockPct}
                                className={cn(
                                  "h-1.5",
                                  stockPct < 50
                                    ? "bg-rose-100"
                                    : "bg-slate-100",
                                )}
                              />
                            </div>
                          </TableCell>
                          <TableCell className="text-right py-2.5">
                            {shortageQty > 0 ? (
                              <div className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-rose-50 text-rose-700 font-bold font-mono text-xs border border-rose-200">
                                <AlertTriangle className="h-3 w-3 shrink-0" />
                                <span>{formatNumber(shortageQty)}</span>
                                <span className="text-[10px] font-normal text-rose-500">
                                  {item.unit}
                                </span>
                              </div>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-emerald-600 text-xs font-medium">
                                <CheckCircle2 className="h-3.5 w-3.5" /> Đủ hàng
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="py-2.5">
                            {item.vendorName ? (
                              <div className="flex flex-col">
                                <span
                                  className="text-xs font-medium text-slate-800 truncate max-w-[140px]"
                                  title={item.vendorName}
                                >
                                  {item.vendorName}
                                </span>
                                {item.vendorCode && (
                                  <span className="text-[10px] text-slate-400 font-mono">
                                    {item.vendorCode}
                                  </span>
                                )}
                              </div>
                            ) : (
                              <span className="text-[11px] text-slate-400 italic">
                                Chưa gán NCC
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="py-2.5">
                            <div className="flex flex-wrap gap-1 max-w-[140px]">
                              {item.relatedPo?.map((po: any, i: number) => {
                                const poId =
                                  typeof po === "object"
                                    ? po.orderId || po.id
                                    : po;
                                return (
                                  <Badge
                                    key={i}
                                    variant="outline"
                                    className="text-[9px] font-bold border-blue-200 text-blue-700 bg-blue-50/70"
                                  >
                                    {poId}
                                  </Badge>
                                );
                              })}
                            </div>
                          </TableCell>
                          <TableCell className="text-center py-2.5">
                            <Badge
                              className={cn(
                                "border-none font-bold text-[10px] shadow-none",
                                statusCfg?.color ||
                                  "bg-slate-100 text-slate-700",
                              )}
                            >
                              {statusCfg?.label || item.status}
                            </Badge>
                          </TableCell>
                          <TableCell
                            className="text-right py-2.5 sticky right-0 z-10 bg-white [isolation:isolate]"
                            onClick={(e: React.MouseEvent) =>
                              e.stopPropagation()
                            }
                          >
                            <div className="flex items-center justify-end gap-1">
                              {shortageQty > 0 &&
                                hasPermission("materialPlanning", "create") && (
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    className="h-7 px-2 text-xs font-semibold text-blue-700 bg-blue-50/70 hover:bg-blue-100 border-blue-200 shadow-none gap-1"
                                    title="Tạo đơn mua hàng cho vật tư này"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setItemsForPO([item]);
                                      setIsBatchPOOpen(true);
                                    }}
                                  >
                                    <ShoppingBag className="h-3 w-3 text-blue-600" />
                                    <span className="hidden xl:inline">Mua</span>
                                  </Button>
                                )}
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7 text-slate-500 hover:text-slate-800"
                                  >
                                    <MoreHorizontal className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent
                                  align="end"
                                  className="w-48"
                                >
                                  <DropdownMenuLabel className="text-xs">
                                    Thao tác
                                  </DropdownMenuLabel>
                                  {hasPermission(
                                    "materialPlanning",
                                    "create",
                                  ) &&
                                    !isChild &&
                                    item.shortageQty > 0 && (
                                      <DropdownMenuItem
                                        onClick={() => setSplitItem(item)}
                                      >
                                        <Split className="mr-2 h-4 w-4" /> Tách
                                        số lượng
                                      </DropdownMenuItem>
                                    )}
                                  {hasPermission(
                                    "materialPlanning",
                                    "create",
                                  ) && (
                                    <DropdownMenuItem
                                      onClick={() => {
                                        setItemsForPO([item]);
                                        setIsBatchPOOpen(true);
                                      }}
                                    >
                                      <ShoppingBag className="mr-2 h-4 w-4" />{" "}
                                      Tạo Đơn Mua Hàng
                                    </DropdownMenuItem>
                                  )}
                                  {hasPermission(
                                    "materialPlanning",
                                    "create",
                                  ) && (
                                    <DropdownMenuItem
                                      onClick={() => setPrItem(item)}
                                    >
                                      <ShoppingBag className="mr-2 h-4 w-4" />{" "}
                                      Tạo PR
                                    </DropdownMenuItem>
                                  )}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          </TableCell>
                        </DataTableRow>
                      );
                    };

                    const renderItemWithSplits = (item: any) => (
                        <React.Fragment key={item.id}>
                          {renderRow(item)}
                          {item.splits?.map((child: any) => {
                            const formattedChild = {
                              ...child,
                              materialCode: item.materialCode,
                              materialName: item.materialName,
                              materialCategory: item.materialCategory,
                              materialType: item.materialType,
                              typeCode: item.typeCode,
                              typeLabel: item.typeLabel,
                              suppliedSize: item.suppliedSize,
                              unit: item.unit,
                              vendorId: item.vendorId,
                              vendorName: item.vendorName,
                              vendorCode: item.vendorCode,
                              unitPrice: item.unitPrice,
                              neededQty: child.qtyRequired,
                              stockQty: child.qtyAvailable,
                              shortageQty: child.qtyToOrder,
                              relatedPo: item.relatedPo,
                            };
                            return renderRow(formattedChild, true);
                          })}
                        </React.Fragment>
                      );

                    return (
                      <DateGroupedTable<any>
                        items={paginatedItems.filter((i: any) => !i.parentId)}
                        isGrouped={isDateGrouped}
                        getDateKey={(item) => extractDateKey(item.createdAt)}
                        renderGroupHeader={(group) => {
                          const shortCount = group.items.filter((i: any) => Number(i.shortageQty) > 0).length;
                          const lsxCount = new Set(
                            group.items.flatMap((i: any) => i.relatedPo || (i.productionOrder?.orderId ? [i.productionOrder.orderId] : [])),
                          ).size;
                          return (
                            <TableRow
                              key={`group-${group.dateKey}`}
                              data-testid="material-plan-date-group"
                              className={cn(
                                "border-y font-semibold text-xs",
                                group.isToday
                                  ? "bg-[#eef2ff] hover:bg-[#e0e7ff] border-indigo-200"
                                  : "bg-[#f1f5f9] hover:bg-[#e2e8f0] border-slate-300",
                              )}
                            >
                              <TableCell colSpan={selectionMode ? 5 : 4} className="py-2 px-3 text-slate-800">
                                <div className="flex items-center gap-2">
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-5 w-5 p-0 text-slate-600 hover:text-slate-900 shrink-0"
                                    onClick={group.toggleCollapse}
                                    title={group.isCollapsed ? "Bung ra" : "Thu gọn"}
                                  >
                                    {group.isCollapsed ? <ChevronRight size={15} /> : <ChevronDown size={15} />}
                                  </Button>
                                  <span className="font-bold text-slate-800 text-[13px] flex items-center gap-1.5">
                                    <Calendar className="w-3.5 h-3.5 text-indigo-600" />
                                    {group.formattedDate}
                                  </span>
                                  <Badge variant="outline" className="bg-white/80 text-slate-700 border-slate-300 text-[11px] font-bold px-2 py-0">
                                    {group.count} vật tư
                                  </Badge>
                                  {group.isToday && (
                                    <Badge className="bg-indigo-600 text-white text-[10px] font-bold px-1.5 py-0 border-0">HÔM NAY</Badge>
                                  )}
                                  <span className="ml-2 whitespace-nowrap" data-testid="material-plan-group-summary">
                                    <span className={cn("text-[11px] font-bold", shortCount > 0 ? "text-rose-700" : "text-emerald-700")}>
                                      {shortCount > 0 ? `Thiếu ${shortCount}/${group.count} vật tư` : "Đủ hàng"}
                                    </span>
                                    {lsxCount > 0 && <span className="text-[11px] text-slate-600 ml-3">{lsxCount} lệnh SX</span>}
                                  </span>
                                </div>
                              </TableCell>
                              <TableCell colSpan={4} className="py-2 px-3" />
                            </TableRow>
                          );
                        }}
                        renderRow={(item: any) => renderItemWithSplits(item)}
                      />
                    );
                  })()
                )}
              </TableBody>
            </Table>
          </ResponsiveDataView>
        </Card>
        )}
      </ListPageShell>

      {!!prItem && (
        <PurchaseRequestForm
          open={!!prItem}
          onOpenChange={(open) => !open && setPrItem(null)}
          onSubmit={handleCreatePR}
          initialData={prItem}
        />
      )}

      {stagingModalData?.open && (
        <ShopfloorMaterialStagingDialog
          open={stagingModalData.open}
          onOpenChange={(open) => {
            if (!open) setStagingModalData(null);
          }}
          productionOrderId={stagingModalData.productionOrderId}
          poCode={stagingModalData.poCode}
          productName={stagingModalData.productName}
          customerName={stagingModalData.customerName}
          materialId={stagingModalData.materialId}
          materialCode={stagingModalData.materialCode}
          materialName={stagingModalData.materialName}
          unit={stagingModalData.unit}
          qtyRequired={stagingModalData.qtyRequired}
          materialPlanId={stagingModalData.materialPlanId}
          onSuccess={async () => {
            toast.success("Cấp phát vật tư sàn máy thành công!");
            await Promise.all([
              refetchPoDemand(),
              queryClient.invalidateQueries({ queryKey: ["material-planning"] }),
              queryClient.invalidateQueries({ queryKey: ["material-planning-stats"] }),
            ]);
          }}
        />
      )}

      <SplitMaterialDialog
        open={!!splitItem}
        onOpenChange={(open) => !open && setSplitItem(null)}
        onSubmit={handleSplitMaterial}
        item={splitItem}
      />

      <PrintModal
        isOpen={isPrintModalOpen}
        onClose={() => setIsPrintModalOpen(false)}
        title="In Kế Hoạch Vật Tư"
        documentTitle="Ke-Hoach-Vat-Tu"
        orientation="landscape"
      >
        <MaterialPlanningPrintContent items={items as any} />
      </PrintModal>

      <BatchCreatePODialog
        open={isBatchPOOpen}
        onOpenChange={setIsBatchPOOpen}
        selectedItems={itemsForPO}
        onSuccess={() => {
          clearSelection();
          queryClient.invalidateQueries({ queryKey: ["material-planning"] });
          queryClient.invalidateQueries({ queryKey: ["material-planning-stats"] });
        }}
      />

      <BulkActionBar
        selectedCount={selectedCount}
        onClearSelection={clearSelection}
        actions={getBulkActions()}
      />

      <MaterialPlanRowDetailSheet
        item={detailItem}
        statusLabel={detailItem?.__statusLabel}
        onOpenChange={(open) => !open && setDetailItem(null)}
      />
    </>
  );
}
